import Box from '@mui/material/Box';
import MuiCircularProgress from '@mui/material/CircularProgress';
import { useFetchers } from '@remix-run/react';
import { fabric } from 'fabric';
import * as React from 'react';
import { deleteIcon, deleteObject, resizeIcon, rotateIcon } from '~/components/DesignLab/common';
import type { Color, ImprintArea } from '~/services/api-utils.server';
import { invertHex } from '~/utils/color';
import { calculateCanvasRatio, round } from '~/utils/common';

function renderIcon(icon: HTMLImageElement, size: number) {
  return function renderIcon(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    styleOverride: any,
    fabricObject: fabric.Object
  ) {
    ctx.save();
    ctx.translate(left, top);
    ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle!));
    ctx.drawImage(icon, -size / 2, -size / 2, size, size);
    ctx.restore();
  };
}

const initCanvas = (canvasId: string, initialValue: string | null, color: string) => {
  if (initialValue) {
    const initFabricCanvas = new fabric.Canvas(canvasId);
    initFabricCanvas.loadFromJSON(initialValue, () => {
      initFabricCanvas.renderAll();
    });
    return initFabricCanvas;
  }

  const initFabricCanvas = new fabric.Canvas(canvasId, {
    backgroundColor: 'transparent',
    renderOnAddRemove: true,
  });

  fabric.Object.prototype.cornerColor = `#${color}`;
  fabric.Object.prototype.borderColor = `#${color}`;
  fabric.Object.prototype.borderDashArray = [3, 3];
  // TODO add padding for small artworks
  //fabric.Object.prototype.padding = 8;

  let deleteImg = document.createElement('img');
  deleteImg.src = deleteIcon(color);
  let rotateImg = document.createElement('img');
  rotateImg.src = rotateIcon(color);
  let resizeImg = document.createElement('img');
  resizeImg.src = resizeIcon(color);

  // Delete controller
  fabric.Object.prototype.controls.tl = new fabric.Control({
    x: -0.5,
    y: -0.5,
    offsetX: -8,
    offsetY: -10,
    cursorStyle: 'pointer',
    sizeX: 16,
    sizeY: 20,
    mouseUpHandler: deleteObject,
    render: renderIcon(deleteImg, 24),
  });
  // Scale controller
  fabric.Object.prototype.controls.br = new fabric.Control({
    x: 0.5,
    y: 0.5,
    offsetX: 12,
    offsetY: 11,
    cursorStyleHandler: fabric.controlsUtils.scaleCursorStyleHandler,
    actionHandler: fabric.controlsUtils.scalingEqually,
    render: renderIcon(resizeImg, 20),
  });
  // Rotate controller
  fabric.Object.prototype.controls.tr = new fabric.Control({
    x: 0.5,
    y: -0.5,
    offsetX: 11,
    offsetY: -7,
    cursorStyle: 'crosshair',
    actionHandler: fabric.controlsUtils.rotationWithSnapping,
    actionName: 'rotate',
    render: renderIcon(rotateImg, 24),
  });

  fabric.Object.prototype.setControlVisible('mr', false);
  fabric.Object.prototype.setControlVisible('mb', false);
  fabric.Object.prototype.setControlVisible('bl', false);
  fabric.Object.prototype.setControlVisible('ml', false);
  fabric.Object.prototype.setControlVisible('mtr', false);
  fabric.Object.prototype.setControlVisible('mt', false);

  return initFabricCanvas;
};

type Canvas2DProps = {
  decoration: number;
  originalImage?: string;
  selectedImage?: string;
  imageBgcolor?: string;
  initialCanvasValue: string | null;
  onSelectElements?: (elements: fabric.Object[] | undefined) => void;
  onAddOrRemoveElements?: (elements: number) => void;
  onModifiedElements: () => void;
  imprintArea: ImprintArea['imprint_area'];
  productColor?: Color;
};
export type Canvas2DRefProps = {
  fabricCanvas: fabric.Canvas | null;
  canvasOuter: HTMLDivElement | null;
};

export const dimensions = { height: 450, width: 450 };

const Canvas2D = React.forwardRef(
  (
    {
      decoration,
      originalImage,
      selectedImage,
      initialCanvasValue,
      onSelectElements,
      onAddOrRemoveElements,
      onModifiedElements,
      imprintArea,
      imageBgcolor,
      productColor,
    }: Canvas2DProps,
    ref: React.ForwardedRef<Canvas2DRefProps>
  ) => {
    const [fabricCanvas, setFabricCanvas] = React.useState<fabric.Canvas | null>(null);

    const handleDeleteOnKeyPress = (event: KeyboardEvent, initCanvasFabric: any) => {
      if (event.key === 'Backspace' || event.key === 'Delete') {
        initCanvasFabric.remove(initCanvasFabric.getActiveObject());
      }
    };

    const fetchers = useFetchers();
    const isColorFetching =
      fetchers.filter((f) => f.submission?.formData.get('_action') === 'changeProductColor').length > 0;

    const initialCanvasValueRef = React.useRef<string | null>(initialCanvasValue);

    const onAddOrRemoveElementsRef = React.useRef(onAddOrRemoveElements);
    React.useEffect(() => {
      onAddOrRemoveElementsRef.current = onAddOrRemoveElements;
    }, [onAddOrRemoveElements]);

    const onModifiedElementsRef = React.useRef(onModifiedElements);
    React.useEffect(() => {
      onModifiedElementsRef.current = onModifiedElements;
    }, [onModifiedElements]);

    React.useEffect(() => {
      const canvasId = `canvas-Id-${decoration}`;
      const initCanvasFabric = initCanvas(
        canvasId,
        initialCanvasValueRef.current,
        invertHex(productColor?.hex.split(';')[0] || 'FFF')
      );

      initCanvasFabric.on('selection:created', ({ selected }) => {
        onSelectElements?.(selected);
      });

      initCanvasFabric.on('selection:updated', ({ selected }) => {
        onSelectElements?.(selected);
      });

      initCanvasFabric.on('selection:cleared', () => {
        onSelectElements?.(undefined);
      });

      initCanvasFabric.on('object:added', () => {
        onAddOrRemoveElementsRef.current?.(initCanvasFabric.getObjects().length);

        window.addEventListener('keydown', (e) => handleDeleteOnKeyPress(e, initCanvasFabric));
      });

      initCanvasFabric.on('object:removed', () => {
        onAddOrRemoveElementsRef.current?.(initCanvasFabric.getObjects().length);
        window.removeEventListener('keydown', (e) => handleDeleteOnKeyPress(e, initCanvasFabric));
      });
      initCanvasFabric.on('object:modified', () => {
        onModifiedElementsRef.current();
      });
      //https://stackoverflow.com/questions/22910496/move-object-within-canvas-boundary-limit
      initCanvasFabric.on('object:moving', function (e) {
        var obj = e.target;
        // if object is too big ignore
        if (obj.currentHeight > obj.canvas.height || obj.currentWidth > obj.canvas.width) {
          return;
        }
        obj.setCoords();
        // top-left  corner
        if (obj.getBoundingRect().top < 0 || obj.getBoundingRect().left < 0) {
          obj.top = Math.max(obj.top, obj.top - obj.getBoundingRect().top);
          obj.left = Math.max(obj.left, obj.left - obj.getBoundingRect().left);
        }
        // bot-right corner
        if (
          obj.getBoundingRect().top + obj.getBoundingRect().height > obj.canvas.height ||
          obj.getBoundingRect().left + obj.getBoundingRect().width > obj.canvas.width
        ) {
          obj.top = Math.min(
            obj.top,
            obj.canvas.height - obj.getBoundingRect().height + obj.top - obj.getBoundingRect().top
          );
          obj.left = Math.min(
            obj.left,
            obj.canvas.width - obj.getBoundingRect().width + obj.left - obj.getBoundingRect().left
          );
        }
      });

      setFabricCanvas(initCanvasFabric);
      return () => {
        initCanvasFabric.removeListeners();
      };
    }, [decoration, onSelectElements, productColor]);

    const canvasOuterRef = React.useRef<HTMLDivElement>(null);

    React.useImperativeHandle(
      ref,
      () => ({
        fabricCanvas,
        canvasOuter: canvasOuterRef.current,
      }),
      [fabricCanvas]
    );

    const [imgDimensions, setImgDimensions] = React.useState({ width: 0, height: 0 });
    const [canvasPosition, setCanvasPosition] = React.useState<{
      top: number;
      bottom: number;
      left: number;
      right: number;
    }>();

    React.useEffect(() => {
      if (originalImage) {
        const img = document.createElement('img');
        img.src = originalImage;

        const getSize = () => {
          const { offset, scaledCanvasPosition, scaledWidth, scaledHeight } = calculateCanvasRatio(
            img.width,
            img.height,
            imprintArea
          );

          if (imprintArea.upper === -1) {
            setCanvasPosition({
              top: 0,
              bottom: dimensions.height,
              left: 0,
              right: dimensions.width,
            });
          } else {
            // calculate offsets
            if (scaledWidth <= scaledHeight) {
              scaledCanvasPosition.left += offset;
              scaledCanvasPosition.right += offset;
            }
            if (scaledWidth >= scaledHeight) {
              scaledCanvasPosition.top += offset;
              scaledCanvasPosition.bottom += offset;
            }

            setCanvasPosition(scaledCanvasPosition);
          }

          setImgDimensions({ width: scaledWidth, height: scaledHeight });
        };
        img.addEventListener('load', getSize);

        return () => {
          img.removeEventListener('load', getSize);
        };
      }
    }, [imprintArea.lower, imprintArea.left, imprintArea.right, imprintArea.upper, originalImage]);

    const canvasHeight = canvasPosition ? round(canvasPosition.bottom - canvasPosition.top) : 0;
    const canvasWidth = canvasPosition ? round(canvasPosition.right - canvasPosition.left) : 0;
    React.useEffect(() => {
      if (fabricCanvas) {
        fabricCanvas.setHeight(canvasHeight);
        fabricCanvas.setWidth(canvasWidth);
      }
    }, [canvasHeight, canvasWidth, fabricCanvas]);

    return (
      <Box
        position="relative"
        display="flex"
        ref={canvasOuterRef}
        id={`canvas-outer-${decoration}`}
        borderRadius={4}
        sx={{
          width: dimensions.width,
          height: dimensions.height,
        }}
      >
        <Box
          sx={{
            margin: 'auto',
            height: imgDimensions.height - 2,
            width: imgDimensions.width - 2,
            backgroundColor: imageBgcolor,
            position: 'relative',
          }}
        >
          <img
            src={selectedImage}
            alt="Background"
            style={{
              opacity: isColorFetching ? 0.3 : 1,
              height: imgDimensions.height,
              width: imgDimensions.width,
              position: 'absolute',
              top: -1,
              left: -1,
            }}
          />
        </Box>
        {isColorFetching && (
          <MuiCircularProgress
            size={60}
            sx={{
              position: 'absolute',
              zIndex: 1,
              top: 200,
              left: 200,
            }}
          />
        )}
        <Box
          id="bounding-box"
          border={canvasPosition ? `1px dashed #${invertHex(productColor?.hex.split(';')[0] || 'FFF')}` : 'none'}
          position="absolute"
          top={canvasPosition?.top}
          left={canvasPosition?.left}
          right={canvasPosition?.right}
          bottom={canvasPosition?.bottom}
          height="fit-content"
          width="fit-content"
        >
          <canvas id={`canvas-Id-${decoration}`} width={canvasWidth} height={canvasHeight} />
        </Box>
      </Box>
    );
  }
);
Canvas2D.displayName = 'Canvas2D';

export default Canvas2D;
